import { Badge, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import {
  AlertTriangleIcon,
  DownloadIcon,
  ExternalLinkIcon,
} from "lucide-react";
import type { SOPDetailDTO } from "@/modules/sop/domain/contracts";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";

interface SOPDetailContentProps {
  sop: SOPDetailDTO;
}

export function SOPDetailContent({ sop }: SOPDetailContentProps) {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-100 text-green-800">ໃຊ້ງານ</Badge>;
      case "draft":
        return <Badge variant="secondary">ຮ່າງ</Badge>;
      case "inactive":
        return <Badge variant="destructive">ບໍ່ໃຊ້ງານ</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 print:space-y-4">
      {/* Section 1: ข้อมูลพื้นฐาน */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{sop.title}</span>
            {getStatusBadge(sop.status)}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ໃຊ້ກັບວຽກຫຍັງ
              </h3>
              <p className="text-sm">{sop.purpose || "-"}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ໃຊ້ກັບພະແນກ
              </h3>
              <p className="text-sm">{sop.departmentName || "-"}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ຜູ້ຮັບຜິດຊອບ
              </h3>
              <p className="text-sm">{sop.responsiblePersonName || "-"}</p>
            </div>
            <div>
              <h3 className="mb-1 font-medium text-muted-foreground text-sm">
                ວັນທີອັບເດດລ່າສຸດ
              </h3>
              <p className="text-sm">{formatDateTimeLocal(sop.updatedAt)}</p>
            </div>
          </div>

          {/* Position Tags */}
          {sop.positionTags && sop.positionTags.length > 0 && (
            <div>
              <h3 className="mb-2 font-medium text-muted-foreground text-sm">
                ຕໍາແໜ່ງທີ່ກ່ຽວຂ້ອງ
              </h3>
              <div className="flex flex-wrap gap-2">
                {sop.positionTags.map((tag, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {tag.positionName}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: จุดประสงค์ */}
      {sop.purpose && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">ຈຸດປະສົງ</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{sop.purpose}</p>
          </CardContent>
        </Card>
      )}

      {/* Section 3: ขอบเขตการใช้งาน */}
      {sop.scope && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">ຂອບເຂດການໃຊ້ງານ</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-line text-sm leading-relaxed">
              {sop.scope}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Section 4: ขั้นตอนการทำงาน */}
      {sop.steps && sop.steps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">ຂັ້ນຕອນການເຮັດວຽກ</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {sop.steps.map((step, index) => (
                <div key={step.id} className="relative">
                  {/* Step number and connector line */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-medium text-primary-foreground text-sm">
                        {step.stepNumber}
                      </div>
                      {index < sop.steps!.length - 1 && (
                        <div className="mx-auto mt-2 h-8 w-0.5 bg-border" />
                      )}
                    </div>

                    <div className="flex-1 space-y-2">
                      <h4 className="font-medium text-sm">{step.title}</h4>

                      {step.description && (
                        <div
                          className="prose prose-sm max-w-none text-muted-foreground text-sm leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: step.description }}
                        />
                      )}

                      {step.responsibleName && (
                        <div className="flex items-center gap-2 text-muted-foreground text-xs">
                          <span>ຜູ້ຮັບຜິດຊອບ:</span>
                          <Badge variant="outline" className="text-xs">
                            {step.responsibleName}
                          </Badge>
                        </div>
                      )}

                      {step.notes && (
                        <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3">
                          <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600" />
                          <div>
                            <p className="mb-1 font-medium text-xs text-yellow-800">
                              ຕ້ອງລະວັງ
                            </p>
                            <p className="text-xs text-yellow-700">
                              {step.notes}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 5: เอกสาร / แบบฟอร์มที่ใช้ */}
      {sop.documents && sop.documents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">ເອກະສານ / ແບບຟອມທີ່ໃຊ້</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sop.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{doc.title}</h4>
                    {doc.fileName && (
                      <p className="text-muted-foreground text-xs">
                        {doc.fileName}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const fileHref = resolveImageSrc(doc.fileKey ?? doc.fileUrl ?? null);
                      return fileHref ? (
                        <>
                          <a
                            href={fileHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1 text-primary-foreground text-xs transition-colors hover:bg-primary/90"
                          >
                            <ExternalLinkIcon className="h-3 w-3" />
                            ເບິ່ງ
                          </a>
                          <a
                            href={fileHref}
                            download={doc.fileName}
                            className="inline-flex items-center gap-1 rounded border border-primary px-3 py-1 text-primary text-xs transition-colors hover:bg-primary hover:text-primary-foreground"
                          >
                            <DownloadIcon className="h-3 w-3" />
                            ດາວໂຫຼດ
                          </a>
                        </>
                      ) : (
                        <span className="text-muted-foreground text-xs">
                          ບໍ່ມີລິ້ງ
                        </span>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 6: ข้อควรระวัง */}
      {sop.warnings && sop.warnings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangleIcon className="h-5 w-5 text-destructive" />
              ຂໍ້ຄວນລະວັງ
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sop.warnings.map((warning, index) => (
                <div
                  key={warning.id}
                  className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-4"
                >
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-destructive font-medium text-destructive-foreground text-xs">
                    {index + 1}
                  </div>
                  <p className="text-sm leading-relaxed">
                    {warning.warningText}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
